(() => {
  if (customElements.get("pawkivo-shipping")) return;

  class PawKivoShipping extends HTMLElement {
    static threshold(rules, country, variants, currency, rate) {
      if (rules?.version !== 1 || !Array.isArray(rules.profiles)) return null;
      if (rules.profiles.some(p => !Array.isArray(p.variants) || !Array.isArray(p.groups))) return null;
      const profiles = variants.length
        ? [...new Set(variants.map(id => rules.profiles.find(p => p.variants.includes(String(id))) || rules.profiles.find(p => p.default)))]
        : rules.profiles.filter(p => p.default);
      if (!profiles.length || profiles.some(p => !p)) return null;
      const digits = new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions().maximumFractionDigits;
      const unit = 10 ** (2 - digits);
      let threshold = 0;
      for (const profile of profiles) {
        let found = false;
        for (const group of profile.groups) {
          const zones = group.filter(z => z.countries.some(c => c.code === country));
          if (!zones.length) zones.push(...group.filter(z => z.countries.some(c => c.restOfWorld)));
          for (const zone of zones) {
            found = true;
            const amounts = zone.offers.map(offer => {
              const amount = Number(offer.amount);
              if (!Number.isFinite(amount) || amount < 0) return null;
              const conversion = offer.currency === currency ? 1 : offer.currency === rules.currency ? Number(rate) : NaN;
              if (!Number.isFinite(conversion) || conversion <= 0) return null;
              return Math.ceil((amount * 100 * conversion) / unit - 1e-8) * unit;
            });
            if (!amounts.length || amounts.some(amount => amount === null)) return null;
            threshold = Math.max(threshold, Math.min(...amounts));
          }
        }
        if (!found) return null;
      }
      return threshold;
    }

    static rules(config) {
      if (!config.url) return null;
      const key = "pawkivo-shipping:v1:" + config.url;
      this.cache ||= new Map();
      let entry = this.cache.get(key);
      if (!entry) {
        try {
          const saved = JSON.parse(sessionStorage.getItem(key));
          if (saved?.expires > Date.now() && saved.rules?.version === 1 && Array.isArray(saved.rules.profiles)) entry = saved;
        } catch { /* 浏览器禁用存储时，仍可使用当前页面的内存缓存。 */ }
        entry ||= {};
        this.cache.set(key, entry);
      }
      if (entry.rules && Date.now() < entry.expires) return entry.rules;
      if (!entry.pending) {
        entry.pending = fetch(config.url, {
          credentials: "omit", cache: "no-store", signal: AbortSignal.timeout(8000),
        }).then(response => {
          if (!response.ok) throw new Error("Shipping rules unavailable");
          return response.json();
        }).then(rules => {
          if (rules?.version !== 1 || !Array.isArray(rules.profiles)) throw new Error("Invalid shipping rules");
          entry.rules = rules;
          entry.expires = Date.now() + 30000;
          try {
            sessionStorage.setItem(key, JSON.stringify({ rules, expires: entry.expires }));
          } catch { /* 存储配额不足不影响配送信息展示。 */ }
          return rules;
        }).catch(() => null).finally(() => { entry.pending = null; });
      }
      return entry.pending;
    }

    connectedCallback() {
      this.refresh();
      if (typeof subscribe === "function") {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => this.refresh());
      }
      if (!PawKivoShipping.timer) {
        PawKivoShipping.timer = setInterval(() => {
          if (document.visibilityState !== "hidden") {
            document.querySelectorAll("pawkivo-shipping").forEach(element => element.refresh());
          }
        }, 30000);
      }
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    async refresh() {
      const configNode = document.getElementById("pawkivo-shipping-config");
      if (!configNode) return;
      const config = JSON.parse(configNode.textContent);
      const result = PawKivoShipping.rules(config);
      const rules = result?.then ? await result : result;
      if (!this.isConnected) return;
      let variants = JSON.parse(this.dataset.variants || "[]");
      const productInfo = this.closest('[id^="ProductInfo-"]') ||
        (this.dataset.mode === "announcement" && variants.length ? document.querySelector('[id^="ProductInfo-"]') : null);
      const selectedVariant = productInfo?.querySelector('product-form [name="id"]')?.value;
      if (selectedVariant) variants = [selectedVariant];
      const minimum = PawKivoShipping.threshold(rules, config.country, variants, config.currency, window.Shopify?.currency?.rate);
      const money = cents => new Intl.NumberFormat(config.locale, {
        style: "currency", currency: config.currency, currencyDisplay: "symbol",
      }).format(cents / 100) + " " + config.currency;
      const text = config.text;
      const amount = minimum === null ? "" : money(minimum);
      const title = minimum === null ? text.fallbackTitle : text.title;
      const detail = minimum === null ? text.fallback : minimum === 0 ? text.unconditional : text.minimum.replace("||amount||", amount);
      const announcement = minimum === null ? text.fallbackAnnouncement : minimum === 0 ? text.unconditional : text.announcement.replace("||amount||", amount);
      this.querySelectorAll("[data-shipping-title]").forEach(node => { node.textContent = title; });
      this.querySelectorAll("[data-shipping-text]").forEach(node => { node.textContent = detail; });
      this.querySelectorAll("[data-shipping-announcement]").forEach(node => { node.textContent = announcement; });
      if (this.dataset.mode === "cart") {
        const total = Number(this.dataset.total);
        const remaining = minimum === null ? null : Math.max(0, minimum - total);
        const message = minimum === null ? text.fallback : remaining > 0
          ? text.remaining.replace("||amount||", money(remaining)) : text.reached;
        this.querySelector("[data-shipping-cart-message]").textContent = message;
        const progress = this.querySelector(".cart-shipping__progress");
        progress.hidden = minimum === null;
        progress.style.display = minimum === null ? "none" : "";
        const percent = minimum === null ? 0 : minimum === 0 ? 100 : Math.min(100, total / minimum * 100);
        this.querySelector(".cart-shipping__progress-current").style.width = percent + "%";
      }
      this.setAttribute("data-ready", "");
    }
  }

  customElements.define("pawkivo-shipping", PawKivoShipping);
})();
